import client, {MutationOptions, QueryOptions} from "@irati/utils/services/apollo"
import {
  Team,
  TeamState,
  TeamUpdateInput,
  Query,
  TeamInput,
  MutationDeleteTeamArgs,
  MutationCreateTeamArgs,
  MutationUpdateTeamArgs,
  QueryTeamsArgs,
  MutationAddUserToTeamArgs,
  MutationRemoveUserFromTeamArgs,
  MutationSetMyBillingUnitArgs
} from "@irati/types"
import PBService from "@irati/utils/services"

import teamErrorHandler from "./errors/legacy/teamErrorHandler"
import listTeamsQuery from "./queries/getTeams"
import getTeamQuery, {teamKey} from "./queries/getTeam"
import addUserToTeamMutation from "./mutations/addUserToTeam"
import removeUserFromTeamMutation from "./mutations/removeUserFromTeam"
import createTeamMutation from "./mutations/createTeam"
import updateTeamMutation from "./mutations/updateTeam"
import deleteTeamMutation from "./mutations/deleteTeam"
import setMyBillingUnitMutation from "./mutations/setMyBillingUnit"
import fullTeamV4 from "./fragments/fullTeamV4"
import teamsPaginated, {teamsPaginatedKey} from "./queries/teamsPaginated"

export interface TeamArgs {
  tenantId: string
  teamId: string
}

export interface TeamsArgs extends Omit<QueryTeamsArgs, "tenantId"> {}

interface TeamListResponse extends Pick<Query, "teams"> {}

class TeamService extends PBService {
  create({tenantId, data}: MutationCreateTeamArgs, options?: MutationOptions): Promise<Team> {
    const variables: MutationCreateTeamArgs = {
      tenantId,
      data: teamInput(data)
    }

    return client.mutate<Team, MutationCreateTeamArgs>({...options, mutation: createTeamMutation, variables}).then((res) => {
      const createdTeam = res.data.createTeam
      addTeamToCache(createdTeam, listVariables(tenantId))
      return createdTeam
    })
  }

  get({tenantId, teamId}: TeamArgs, options?: QueryOptions): Promise<Team> {
    const variables = {
      tenantId,
      teamId
    }

    return client.query({...options, query: getTeamQuery, variables}).then(({data}) => data[teamKey])
  }

  update(tenantId: string, team: TeamUpdateInput, options?: MutationOptions): Promise<Team> {
    const variables = {
      tenantId,
      totalOnly: true,
      data: teamUpdateInput(team)
    }

    return client
      .mutate<any, MutationUpdateTeamArgs>({...options, mutation: updateTeamMutation, variables})
      .then(({data}) => data.updateTeam)
  }

  delete(tenantId: string, teamId: string, options?: MutationOptions): Promise<boolean> {
    const variables = {
      tenantId,
      teamId
    }

    return client.mutate<boolean, MutationDeleteTeamArgs>({...options, mutation: deleteTeamMutation, variables}).then(() => {
      /**
       * @description: On delete we want to remove team from lists on the UI. Setting teamState to "Deleted" and filtering on watchList.
       *
       * This should be replaced with a simple client.cache.evict(`Team@${teamId}`) when we update to Apollo V3.
       */
      const currentData = client.readFragment({id: `Team@${teamId}`, fragment: fullTeamV4, fragmentName: "fullTeam"})
      client.writeFragment({
        id: `Team@${teamId}`,
        fragment: fullTeamV4,
        fragmentName: "fullTeam",
        data: {
          ...currentData,
          teamState: TeamState.Deleted
        }
      })
      return true
    })
  }

  teamsPaginated(
    businessId: string,
    teamName?: string,
    limit?: string | number,
    searchAfter?: string,
    state?: TeamState,
    options?: QueryOptions,
    hasBranding?: boolean,
    hasManager?: boolean
  ): Promise<Team[]> {
    const variables = {businessId, teamName, limit, searchAfter, state, hasBranding, hasManager}
    return client.query({...options, query: teamsPaginated, variables}).then(({data}) => {
      return data[teamsPaginatedKey]
    })
  }

  addUserToTeam(teamId: string, userId: string, assignment: string, options?: MutationOptions) {
    const variables = {
      teamId,
      userId,
      assignment
    }

    return client
      .mutate<any, MutationAddUserToTeamArgs>({...options, mutation: addUserToTeamMutation, variables})
      .then(({data}) => data.addUserToTeam)
  }

  removeUserFromTeam(teamId: string, userId: string, options?: MutationOptions) {
    const variables = {
      teamId,
      userId
    }

    return client
      .mutate<any, MutationRemoveUserFromTeamArgs>({...options, mutation: removeUserFromTeamMutation, variables})
      .then(({data}) => data.removeUserFromTeam)
  }
  setMyBillingUnit(teamId: string, options?: MutationOptions) {
    const variables = {
      teamId
    }

    return client
      .mutate<any, MutationSetMyBillingUnitArgs>({...options, mutation: setMyBillingUnitMutation, variables})
      .then(({data}) => data.setMyBillingUnit)
  }
}

const teamsService = new TeamService({errorHandlers: [teamErrorHandler]})

export default teamsService

function teamInput(team: any): TeamInput {
  const data: TeamInput = {
    public_name: team.teamName || team.public_name,
    permissions: team.permissions,
    owners: team.owners,
    teamState: team.teamState || team.state,
    teamCategory: team.teamCategory
  }

  return data
}

function teamUpdateInput(team: any): TeamUpdateInput {
  const data: TeamUpdateInput = {
    uuid: team.uuid,
    public_name: team.public_name,
    teamState: team.teamState || team.state,
    teamCategory: team.teamCategory
  }

  return data
}

function listVariables(tenantId: string, state: TeamState = TeamState.All): QueryTeamsArgs {
  return {
    tenantId,
    state
  }
}

function addTeamToCache(createdTeam: Team, variables: QueryTeamsArgs) {
  try {
    const cachedData = client.readQuery<TeamListResponse>({query: listTeamsQuery, variables})
    if (cachedData?.teams) {
      const results = [createdTeam, ...cachedData.teams.results]
      client.writeQuery({
        query: listTeamsQuery,
        variables,
        data: {
          teams: {
            ...cachedData.teams,
            total: results.length,
            results
          }
        }
      })
    }
  } catch (e) {
    /* no-op */
  }
}
